<template>
  <div>
    <p class="cg-micro mb-2">
      Consumption · {{ fmt(domain.consumption.fy) }} MWh
    </p>
    <StackedBar :segments="consumptionSegments" class="mb-2" />
    <div class="d-flex gap-3 mb-4 text-body-secondary small">
      <span class="d-flex align-items-center gap-2"
        ><span
          class="cg-swatch"
          :style="{ background: FILL.inAccount }"
        />Actual {{ fmt(domain.consumption.reported) }}</span
      >
      <span class="d-flex align-items-center gap-2"
        ><span
          class="cg-swatch"
          :style="{ background: FILL.estimated }"
        />Estimated {{ fmt(domain.consumption.estimated) }}</span
      >
    </div>

    <p class="cg-micro mb-2">
      Earmarked GOs · {{ fmt(domain.earmarkedTotal) }} MWh
    </p>
    <StackedBar :segments="earmarkedSegments" class="mb-2" />
    <div class="d-flex flex-wrap gap-3 text-body-secondary small">
      <span class="d-flex align-items-center gap-2"
        ><span
          class="cg-swatch"
          :style="{ background: FILL.cancelled }"
        />Locked {{ fmt(domain.earmarked.locked) }}</span
      >
      <span class="d-flex align-items-center gap-2"
        ><span class="cg-swatch" :style="{ background: FILL.inAccount }" />In
        account {{ fmt(domain.earmarked.flexible) }}</span
      >
      <span class="d-flex align-items-center gap-2"
        ><span
          class="cg-swatch"
          :style="{ background: FILL.expected }"
        />Expected {{ fmt(domain.earmarked.expected) }}</span
      >
      <span class="d-flex align-items-center gap-2"
        ><span class="cg-swatch" :style="{ background: FILL.shortfall }" />Gap
        {{ fmt(Math.max(0, domain.gap)) }}</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DerivedDomain } from "@/compliance_groups/fixture/derive";
import { FILL } from "@/compliance_groups/fixture/fills";
import { fmt, pctOf } from "@/compliance_groups/fixture/format";
import { computed } from "vue";
import StackedBar from "./StackedBar.vue";

const props = defineProps<{ domain: DerivedDomain }>();

const consumptionSegments = computed(() => [
  {
    fill: "inAccount" as const,
    pct: pctOf(props.domain.consumption.reported, props.domain.consumption.fy),
  },
  {
    fill: "estimated" as const,
    pct: pctOf(props.domain.consumption.estimated, props.domain.consumption.fy),
  },
]);
const earmarkedSegments = computed(() => {
  const total = props.domain.earmarkedTotal + Math.max(0, props.domain.gap);
  return [
    {
      fill: "cancelled" as const,
      pct: pctOf(props.domain.earmarked.locked, total),
    },
    {
      fill: "inAccount" as const,
      pct: pctOf(props.domain.earmarked.flexible, total),
    },
    {
      fill: "expected" as const,
      pct: pctOf(props.domain.earmarked.expected, total),
    },
    {
      fill: "shortfall" as const,
      pct: pctOf(Math.max(0, props.domain.gap), total),
    },
  ];
});
</script>

<style scoped lang="scss">
.cg-swatch {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  display: inline-block;
}
</style>
